/**
 * `/m/:token` — the door, and the one page with no sign-in gate.
 *
 * The link **is** the credential. Somebody arriving here may have no account,
 * so the route is matched before the gate in `App.tsx` and redeeming the token
 * is `POST /api/relay/meetings/join` — the only route in relay-server that
 * answers without a session, and it takes the token in the **body**, never the
 * path, because request URLs are retained in observability.
 *
 * ── Two visitors, one door ──────────────────────────────────────────────────
 * A signed-in visitor joins under their account and is never asked for a name:
 * the server mints no guest session for somebody who has one, and a name box in
 * front of them would collect a string it then throws away. Everybody else
 * types a name, and that name is self-asserted for the rest of the meeting —
 * every surface draws it through `participantLabel`, which appends "(Guest)".
 *
 * ── Knocking is a poll, not a socket ────────────────────────────────────────
 * Three seconds, the same cadence as the roster. A guest holds no socket until
 * they are in the room, and opening one to learn a yes/no would be a second
 * connection whose only frame is the one this poll already asks for.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ds } from '../ds';
import { useT } from '../i18n';
import { fetchMe } from '../lib/api';
import { participantLabel } from '../lib/relay/types';
import type { Admission, Person } from '../lib/relay/types';
import { MeetingRoom } from '../components/meeting/MeetingRoom';
import { joinByToken, myStanding } from '../components/meeting/api';

const POLL_MS = 3000;

type Door =
  | { step: 'checking' }
  | { step: 'name'; me: Person | null }
  | { step: 'joining'; me: Person | null }
  | {
      step: 'standing';
      meetingId: string;
      title: string;
      displayName: string;
      guest: boolean;
      token?: string;
      state: Admission;
      ended: boolean;
    }
  | { step: 'gone' };

export function GuestPage({ token }: { token: string }) {
  const { Button, Input, Wordmark } = ds();
  const t = useT();
  const [door, setDoor] = useState<Door>({ step: 'checking' });
  const [name, setName] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (!cancelled) setDoor({ step: 'name', me });
      })
      .catch(() => {
        // Unreachable accounts are not a closed door: the meeting may still
        // take a guest, and that is the path this page exists for.
        if (!cancelled) setDoor({ step: 'name', me: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const join = useCallback(
    (me: Person | null) => {
      setDoor({ step: 'joining', me });
      void joinByToken(token, me ? undefined : name.trim())
        .then((answer) =>
          setDoor({
            step: 'standing',
            meetingId: answer.meetingId,
            title: answer.title,
            displayName: answer.displayName,
            guest: answer.guest,
            ...(answer.meetingToken ? { token: answer.meetingToken } : {}),
            state: answer.state,
            ended: answer.status === 'ended'
          })
        )
        .catch(() => setDoor({ step: 'gone' }));
    },
    [token, name]
  );

  // The knock, answered by the host or not. Stops the moment it is not
  // `knocking` — a denied party polling on is a party being told no repeatedly.
  useEffect(() => {
    if (door.step !== 'standing' || door.state !== 'knocking') return;
    const { meetingId, token: guestToken } = door;
    const timer = window.setInterval(() => {
      void myStanding(meetingId, guestToken)
        .then((standing) =>
          setDoor((current) =>
            current.step === 'standing'
              ? { ...current, state: standing.state, ended: standing.status === 'ended' }
              : current
          )
        )
        .catch(() => {
          /* A meeting that ended answers 404 while the poll is in flight. The
             next tick either recovers or the host ended it, and the room says
             so; a thrown error here would empty the page under somebody who is
             simply waiting. */
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [door]);

  if (door.step === 'standing') {
    return (
      <div className="mv-app" style={{ height: '100vh', display: 'flex' }}>
        <MeetingRoom
          meetingId={door.meetingId}
          title={door.title}
          role="attendee"
          admission={door.state}
          ended={door.ended}
          {...(door.guest && door.token
            ? { guest: { token: door.token, displayName: door.displayName } }
            : {})}
          onLeave={() => setDoor({ step: 'gone' })}
        />
      </div>
    );
  }

  return (
    <Frame>
      <Wordmark size={24} />
      <h1
        style={{
          fontFamily: 'var(--font-display), sans-serif',
          fontWeight: 800,
          fontSize: 28,
          lineHeight: 1.1,
          margin: 'var(--space-16) 0 var(--space-20)'
        }}
      >
        {t('guest.title')}
      </h1>

      {door.step === 'checking' || door.step === 'joining' ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('app.loading')}</p>
      ) : door.step === 'gone' ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('call.guest.gone')}</p>
      ) : door.me ? (
        <>
          <p style={{ color: 'var(--text-muted)' }}>{door.me.name}</p>
          <div style={{ marginTop: 'var(--space-20)' }}>
            <Button fullWidth onClick={() => join(door.me)}>
              {t('guest.join')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Input
            label={t('guest.name.label')}
            value={name}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
          />
          {name.trim() && (
            <p style={{ color: 'var(--text-faint)', fontSize: 13, marginTop: 'var(--space-8)' }}>
              {participantLabel({ displayName: name.trim(), guest: true })}
            </p>
          )}
          <div style={{ marginTop: 'var(--space-20)' }}>
            <Button fullWidth onClick={() => join(null)} disabled={!name.trim()}>
              {t('guest.join')}
            </Button>
          </div>
        </>
      )}
    </Frame>
  );
}

/** The card the door is drawn on. `mv-app` is deliberate here and only here on
 *  this page: it is the shell's sunken-input rule, and the room below needs it
 *  as much as the app does. */
function Frame({ children }: { children: ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-24)',
        background: 'var(--app-ground)'
      }}
    >
      <div
        style={{
          width: 'min(420px, 100%)',
          padding: 'var(--space-32)',
          borderRadius: 'var(--radius-2xl)',
          background: 'var(--surface-card)',
          boxShadow: 'var(--shadow-hairline)'
        }}
      >
        {children}
      </div>
    </div>
  );
}
